import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import RoleBasedNavigation from "../../components/ui/RoleBasedNavigation";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Icon from "../../components/AppIcon";
import BarcodeScanner from "../../components/ui/BarcodeScanner";
import { customers as customersAPI } from "../../lib/database";
import { useToast } from "../../components/ui/Toast";

const STATUS_LABELS = {
  survey_not_started: "Survey Not Started",
  survey_inprogress: "Survey In Progress",
  survey_complete: "Survey Complete",
  survey_unworkable: "Survey Unworkable",
  transport_pending: "Transport Pending",
  transport_inprogress: "Transport In Progress",
  transport_complete: "Transport Complete",
  civil_pending: "Civil Pending",
  civil_inprogress: "Civil In Progress",
  civil_complete: "Civil Complete",
  technical_pending: "Technical Pending",
  technical_inprogress: "Technical In Progress",
  technical_complete: "Technical Complete",
  commissioning_not_started: "Commissioning Not Started",
  commissioning_inprogress: "Commissioning In Progress",
  commissioning_complete: "Commissioning Complete",
};

const getPhaseState = (overallStatus, phaseKey) => {
  const s = String(overallStatus || "").trim();
  if (!s) return "not_started";

  if (phaseKey === "survey") {
    if (s === "survey_not_started") return "not_started";
    if (s === "survey_inprogress") return "inprogress";
    return "complete";
  }

  if (phaseKey === "transport") {
    if (s === "transport_inprogress") return "inprogress";
    if (
      [
        "transport_complete",
        "civil_pending",
        "civil_inprogress",
        "civil_complete",
        "technical_pending",
        "technical_inprogress",
        "technical_complete",
        "commissioning_not_started",
        "commissioning_inprogress",
        "commissioning_complete",
      ].includes(s)
    ) {
      return "complete";
    }
    return "not_started";
  }

  if (phaseKey === "civil") {
    if (s === "civil_inprogress") return "inprogress";
    if (
      [
        "civil_complete",
        "technical_pending",
        "technical_inprogress",
        "technical_complete",
        "commissioning_not_started",
        "commissioning_inprogress",
        "commissioning_complete",
      ].includes(s)
    ) {
      return "complete";
    }
    return "not_started";
  }

  if (phaseKey === "technical") {
    if (s === "technical_inprogress") return "inprogress";
    if (
      [
        "technical_complete",
        "commissioning_not_started",
        "commissioning_inprogress",
        "commissioning_complete",
      ].includes(s)
    ) {
      return "complete";
    }
    return "not_started";
  }

  if (phaseKey === "commissioning") {
    if (s === "commissioning_inprogress") return "inprogress";
    if (s === "commissioning_complete") return "complete";
    return "not_started";
  }

  return "not_started";
};

const getActionText = (phaseState) => {
  if (phaseState === "inprogress") return "Resume";
  if (phaseState === "complete") return "Edit";
  return "Start";
};

const WorkerDashboard = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [lookup, setLookup] = useState("");
  const [loading, setLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [customer, setCustomer] = useState(null);

  const normalize = (v) => String(v || "").trim();

  const resolveCustomer = async (rawIdentifier) => {
    const identifier = normalize(rawIdentifier);
    if (!identifier) return null;

    const directByCin = await customersAPI.getByCIN(identifier).catch(() => null);
    if (directByCin) return directByCin;

    const directByUsn = await customersAPI.getByUSN(identifier).catch(() => null);
    if (directByUsn) return directByUsn;

    const searchHits = await customersAPI.search(identifier).catch(() => []);
    if (!Array.isArray(searchHits) || searchHits.length === 0) return null;

    const exact = searchHits.find((c) => {
      const cin = normalize(c?.cin).toLowerCase();
      const usn = normalize(c?.usn).toLowerCase();
      const phone = normalize(c?.phone_number);
      const token = identifier.toLowerCase();
      return cin === token || usn === token || phone === identifier;
    });
    return exact || searchHits[0];
  };

  const performLookup = async (identifier = lookup) => {
    const value = normalize(identifier);
    if (!value) {
      showToast("Enter CIN / USN / phone number.", "info");
      return;
    }
    try {
      setLoading(true);
      const found = await resolveCustomer(value);
      if (!found) {
        setCustomer(null);
        showToast("No customer found for this identifier.", "error");
        return;
      }
      setCustomer(found);
    } catch (error) {
      console.error("Worker lookup failed:", error);
      showToast("Failed to fetch customer.", "error");
    } finally {
      setLoading(false);
    }
  };

  const onScan = (value) => {
    setShowScanner(false);
    setLookup(value || "");
    performLookup(value);
  };

  const handleOpenDirections = () => {
    const lat = Number(customer?.latitude);
    const lng = Number(customer?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      showToast("Customer coordinates are not available.", "error");
      return;
    }
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
      `${lat},${lng}`,
    )}`;
    window.open(mapsUrl, "_blank", "noopener,noreferrer");
  };

  const actions = useMemo(() => {
    const status = customer?.status || "";
    const base = customer?.id ? `customerId=${encodeURIComponent(customer.id)}` : "";
    return [
      {
        key: "survey",
        phase: "Survey",
        icon: "ClipboardList",
        enabled: !!customer?.id,
        hint: "Always available once customer is selected.",
        phaseState: getPhaseState(status, "survey"),
        onClick: () => navigate(`/customer-survey?${base}`),
      },
      {
        key: "transport",
        phase: "Transport",
        icon: "Truck",
        enabled: !!customer?.id,
        hint: "Open and work on transport phase.",
        phaseState: getPhaseState(status, "transport"),
        onClick: () => navigate(`/transport-workflow?${base}&mode=edit`),
      },
      {
        key: "civil",
        phase: "Civil",
        icon: "Wrench",
        enabled: !!customer?.id,
        hint: "Open and work on civil phase.",
        phaseState: getPhaseState(status, "civil"),
        onClick: () => navigate(`/installation-workflow?${base}&phase=civil`),
      },
      {
        key: "technical",
        phase: "Technical",
        icon: "Zap",
        enabled: !!customer?.id,
        hint: "Open and work on technical phase.",
        phaseState: getPhaseState(status, "technical"),
        onClick: () => navigate(`/installation-workflow?${base}&phase=technical`),
      },
      {
        key: "commissioning",
        phase: "Commissioning",
        icon: "CheckCircle2",
        enabled: !!customer?.id,
        hint: "Open and work on commissioning phase.",
        phaseState: getPhaseState(status, "commissioning"),
        onClick: () => navigate(`/commissioning-workflow?${base}`),
      },
    ];
  }, [customer, navigate]);

  return (
    <>
      <Helmet>
        <title>Worker Dashboard</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />
        <div className="content-area">
          <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="mb-6">
              <h1 className="text-2xl md:text-3xl font-semibold text-foreground">
                Worker Dashboard
              </h1>
              <p className="text-sm md:text-base text-muted-foreground mt-1">
                Find customer by CIN / USN / phone or scan QR, then open any phase.
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3">
                <Input
                  label="Customer Identifier"
                  placeholder="Enter CIN, USN, or phone number"
                  value={lookup}
                  onChange={(e) => setLookup(e.target.value)}
                />
                <Button
                  variant="outline"
                  iconName="QrCode"
                  iconPosition="left"
                  onClick={() => setShowScanner(true)}
                  className="md:self-end"
                >
                  Scan QR
                </Button>
                <Button
                  variant="default"
                  iconName="Search"
                  iconPosition="left"
                  loading={loading}
                  onClick={() => performLookup()}
                  className="md:self-end"
                >
                  Search
                </Button>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4 md:p-6">
              {!customer ? (
                <div className="text-center py-10">
                  <Icon
                    name="Search"
                    size={40}
                    className="mx-auto text-muted-foreground mb-2"
                  />
                  <p className="text-muted-foreground">
                    Search and select a customer to view actions.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-5 p-4 rounded-lg bg-muted/40 border border-border">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <p className="text-sm font-medium text-foreground">
                        Customer Details
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        iconName="Navigation"
                        iconPosition="left"
                        onClick={handleOpenDirections}
                      >
                        Directions
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <p>
                        <span className="text-muted-foreground">Name:</span>{" "}
                        <span className="font-medium text-foreground">
                          {customer.name || "—"}
                        </span>
                      </p>
                      <p>
                        <span className="text-muted-foreground">CIN:</span>{" "}
                        <span className="font-medium text-foreground">
                          {customer.cin || "—"}
                        </span>
                      </p>
                      <p>
                        <span className="text-muted-foreground">USN:</span>{" "}
                        <span className="font-medium text-foreground">
                          {customer.usn || "—"}
                        </span>
                      </p>
                      <p>
                        <span className="text-muted-foreground">Status:</span>{" "}
                        <span className="font-medium text-foreground">
                          {STATUS_LABELS[customer.status] || customer.status || "—"}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    {actions.map((action) => (
                      <div
                        key={action.key}
                        className="border border-border rounded-lg p-4 flex items-center justify-between gap-3"
                      >
                        <div className="min-w-0 flex items-start gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Icon
                              name={action.icon}
                              size={18}
                              className="text-primary"
                            />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-foreground">
                              {action.phase}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {action.hint}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                            {action.phaseState === "inprogress"
                              ? "In Progress"
                              : action.phaseState === "complete"
                                ? "Complete"
                                : "Not Started"}
                          </span>
                          <Button
                            size="sm"
                            variant={action.enabled ? "default" : "outline"}
                            disabled={!action.enabled}
                            iconName={action.icon}
                            iconPosition="left"
                            onClick={action.onClick}
                          >
                            {getActionText(action.phaseState)}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {showScanner && <BarcodeScanner onScan={onScan} onClose={() => setShowScanner(false)} />}
    </>
  );
};

export default WorkerDashboard;
